import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useClickOutside } from "../hooks/useClickOutside";

interface SidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  width?: string; // e.g., "800px", "50%", "max-w-4xl"
}

/**
 * A slide-out side panel component that appears from the right side of the screen.
 * Includes a backdrop overlay and close button.
 */
export function SidePanel({
  isOpen,
  onClose,
  title,
  children,
  width = "800px",
}: SidePanelProps) {
  const panelRef = React.useRef<HTMLDivElement>(null);

  // Close panel when clicking outside
  useClickOutside(panelRef, () => {
    if (isOpen) {
      onClose();
    }
  }, isOpen);

  // Prevent body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <>
      {/* Backdrop overlay - semi-transparent so page content remains visible */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Side panel */}
      <div
        ref={panelRef}
        className="fixed right-0 top-0 h-full bg-card shadow-lg border-l border-border z-50 flex flex-col transition-transform duration-300 ease-in-out"
        style={{ width: width, maxWidth: "90vw" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="side-panel-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border flex-shrink-0 bg-card">
          <h2 id="side-panel-title" className="text-lg font-semibold text-foreground">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="simple-icon-button"
            aria-label="Close panel"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content area - scrollable */}
        <div className="flex-1 overflow-y-auto p-5 bg-muted">
          {children}
        </div>
      </div>
    </>,
    document.body
  );
}
